import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../helpers'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
    AdminSchoolRow,
    Provider,
    ProspectEnrichmentOutputCost,
    SchoolPoolStatsRow,
} from '../../models/Outreach/types'

function poolQueueError(error: unknown, fallback: string): string {
    const response = (error as {
        response?: { status?: number; data?: { error?: string } }
    }).response
    if (response?.status === 409 && response.data?.error) {
        return response.data.error
    }
    return fallback
}

export const useOutreach = (options?: {
    loadProviders?: boolean
    loadSchoolPool?: boolean
    loadAdminSchools?: boolean
    loadTokenCosts?: boolean
}) => {
    const loadProviders = options?.loadProviders ?? false
    const loadSchoolPool = options?.loadSchoolPool ?? false
    const loadAdminSchools = options?.loadAdminSchools ?? false
    const loadTokenCosts = options?.loadTokenCosts ?? false
    const queryClient = useQueryClient()

    const {
        data: providersResponse,
        isLoading: isLoadingProviders,
        error: errorProviders,
    } = useQuery<AxiosResponse<Provider[]>, Error>({
        queryKey: ['providers'],
        queryFn: () => axiosInstance.get(`/outreach/providers/`),
        enabled: loadProviders,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createProvider = useMutation({
        mutationFn: (provider: Partial<Provider>) =>
            axiosInstance.post('/outreach/providers/', provider),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['providers'],
            })
            toast.success('Provider created successfully')
        },
        onError: (error) => {
            toast.error('Failed to create provider')
        },
    })

    const updateProvider = useMutation({
        mutationFn: ({
            id,
            updates,
        }: {
            id: string | number
            updates: Partial<Provider>
        }) => axiosInstance.patch(`/outreach/providers/${id}/`, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] })
            toast.success('Provider updated successfully')
        },
        onError: () => {
            toast.error('Failed to update provider')
        },
    })

    const deleteProvider = useMutation({
        mutationFn: (id: string | number) =>
            axiosInstance.delete(`/outreach/providers/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] })
            toast.success('Provider deleted successfully')
        },
        onError: () => {
            toast.error('Failed to delete provider')
        },
    })

    const {
        data: schoolPoolStatsResponse,
        isLoading: isLoadingSchoolPoolStats,
        error: errorSchoolPoolStats,
    } = useQuery<AxiosResponse<SchoolPoolStatsRow[]>, Error>({
        queryKey: ['school-pool-stats'],
        queryFn: () => axiosInstance.get(`/outreach/school-pool-stats/`),
        enabled: loadSchoolPool,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const {
        data: adminSchoolsResponse,
        isLoading: isLoadingAdminSchools,
        error: errorAdminSchools,
    } = useQuery<AxiosResponse<AdminSchoolRow[]>, Error>({
        queryKey: ['admin-schools'],
        queryFn: () => axiosInstance.get(`/outreach/schools/`),
        enabled: loadAdminSchools,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const updateAdminSchoolOverride = useMutation({
        mutationFn: ({
            id,
            ezos_admin_display_name_override,
        }: {
            id: number
            ezos_admin_display_name_override: string
        }) =>
            axiosInstance.patch(`/outreach/schools/${id}/`, {
                ezos_admin_display_name_override,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-schools'] })
            queryClient.invalidateQueries({ queryKey: ['school-pool-stats'] })
            toast.success('School EZOS display name updated')
        },
        onError: () => {
            toast.error('Failed to update school display name')
        },
    })

    const runSchoolPoolSearch = useMutation({
        mutationFn: ({
            schoolId,
            limit,
        }: {
            schoolId: number
            limit: number
        }) =>
            axiosInstance.post(
                `/outreach/school-pool-stats/${schoolId}/run-pool-search/`,
                { limit },
            ),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['school-pool-stats'] })
            toast.success(
                `Queued one page of up to ${variables.limit} profiles`,
            )
        },
        onError: (error: unknown) => {
            toast.error(poolQueueError(error, 'Failed to queue pool search'))
        },
    })

    const grabSchoolPool = useMutation({
        mutationFn: (schoolId: number) =>
            axiosInstance.post(
                `/outreach/school-pool-stats/${schoolId}/grab-pool/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['school-pool-stats'] })
            toast.success('Queued AMAP for this school')
        },
        onError: (error: unknown) => {
            toast.error(poolQueueError(error, 'Failed to queue AMAP'))
        },
    })

    useEffect(() => {
        const rows = schoolPoolStatsResponse?.data
        if (
            !Array.isArray(rows) ||
            !rows.some((row) => row.pool_search_in_flight)
        ) {
            return
        }
        const id = window.setInterval(() => {
            queryClient.invalidateQueries({ queryKey: ['school-pool-stats'] })
        }, 5000)
        return () => window.clearInterval(id)
    }, [schoolPoolStatsResponse, queryClient])

    const {
        data: enrichmentTokenCostsResponse,
        isLoading: isLoadingEnrichmentTokenCosts,
        error: errorEnrichmentTokenCosts,
    } = useQuery<AxiosResponse<ProspectEnrichmentOutputCost[]>, Error>({
        queryKey: ['enrichment-token-costs'],
        queryFn: () => axiosInstance.get(`/outreach/enrichment-token-costs/`),
        enabled: loadTokenCosts,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    })

    const updateEnrichmentTokenCosts = useMutation({
        mutationFn: (cost: { output_field: string; token_cost: number }) =>
            axiosInstance.post(`/outreach/enrichment-token-costs/`, {
                costs: cost,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['enrichment-token-costs'],
            })
            toast.success('Enrichment token costs updated successfully')
        },
        onError: (error) => {
            toast.error('Failed to update enrichment token costs')
        },
    })

    return {
        providers: providersResponse?.data,
        isLoadingProviders,
        errorProviders,
        createProvider,
        updateProvider,
        deleteProvider,
        schoolPoolStats: schoolPoolStatsResponse?.data,
        isLoadingSchoolPoolStats,
        errorSchoolPoolStats,
        adminSchools: adminSchoolsResponse?.data,
        isLoadingAdminSchools,
        errorAdminSchools,
        updateAdminSchoolOverride,
        runSchoolPoolSearch,
        grabSchoolPool,
        enrichmentTokenCosts: enrichmentTokenCostsResponse?.data,
        isLoadingEnrichmentTokenCosts,
        errorEnrichmentTokenCosts,
        updateEnrichmentTokenCosts,
    }
}
